import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { documentAPI, TripDocument } from '../../api/documents';

interface DocumentPreviewModalProps {
  document: TripDocument;
  onClose: () => void;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document,
  onClose,
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const blob = await documentAPI.download(document.id);
        const url = window.URL.createObjectURL(blob);
        setImageUrl(url);
      } catch (err) {
        setError(t('documents.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (document.mimeType.startsWith('image/')) {
      loadImage();
    } else {
      setLoading(false);
    }

    return () => {
      if (imageUrl) {
        window.URL.revokeObjectURL(imageUrl);
      }
    };
  }, [document.id, document.mimeType, t]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-medium text-gray-900 truncate pr-4">
            {document.fileName}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-auto max-h-[calc(90vh-60px)] flex items-center justify-center">
          {loading ? (
            <div className="text-gray-500 py-8">{t('common.loading')}</div>
          ) : error ? (
            <div className="text-red-500 py-8">{error}</div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={document.fileName}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="text-gray-500 py-8">{t('documents.cannotPreview')}</div>
          )}
        </div>
      </div>
    </div>
  );
};
